import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import WorkIcon from "@mui/icons-material/Work";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import HorizontalBarChart from "examples/Charts/BarCharts/HorizontalBarChart";
import PieChart from "examples/Charts/PieChart";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS } from "config/api";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alumni-analytics-tabpanel-${index}`}
      aria-labelledby={`alumni-analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const AdminAlumniAnalysisDialog = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axiosInstance
      .get(API_ENDPOINTS.ADMIN.GET_ALUMNI_ANALYTICS)
      .then((response) => {
        setAnalyticsData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching alumni analytics:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Prepare chart data helpers for ReportsBarChart/ReportsLineChart
  const prepareChartData = (labels, values, label) => {
    return {
      labels: labels || [],
      datasets: {
        label: label || "Count",
        data: values || [],
      },
    };
  };

  // Prepare chart data for VerticalBarChart (needs array with color)
  const prepareVerticalBarChartData = (labels, values, color = "info") => {
    return {
      labels: labels || [],
      datasets: [
        {
          label: "Count",
          data: values || [],
          color: color,
        },
      ],
    };
  };

  // Prepare chart data for PieChart (needs object with label and data)
  const preparePieChartData = (labels, values, label = "Distribution") => {
    return {
      labels: labels || [],
      datasets: {
        label: label,
        data: values || [],
      },
    };
  };

  // Batch-wise Charts
  const batchAlumniChart = analyticsData
    ? prepareChartData(
        analyticsData.batchWise?.alumniByBatch?.labels,
        analyticsData.batchWise?.alumniByBatch?.values,
        "Total Alumni"
      )
    : { labels: [], datasets: { label: "", data: [] } };

  const batchRegisteredChart = analyticsData
    ? prepareChartData(
        analyticsData.batchWise?.registeredByBatch?.labels,
        analyticsData.batchWise?.registeredByBatch?.values,
        "Registered Alumni"
      )
    : { labels: [], datasets: { label: "", data: [] } };

  const batchUnregisteredChart = analyticsData
    ? prepareChartData(
        analyticsData.batchWise?.unregisteredByBatch?.labels,
        analyticsData.batchWise?.unregisteredByBatch?.values,
        "Unregistered Alumni"
      )
    : { labels: [], datasets: { label: "", data: [] } };

  // Combination Reports Chart
  const batchDistrictChart = analyticsData
    ? prepareChartData(
        analyticsData.combinationReports?.batchVsDistrict?.labels?.slice(0, 20),
        analyticsData.combinationReports?.batchVsDistrict?.values?.slice(0, 20),
        "Alumni Count"
      )
    : { labels: [], datasets: { label: "", data: [] } };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen>
      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
        <IconButton onClick={onClose} color="error">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <PeopleIcon color="primary" />
          <MDTypography variant="h4" fontWeight="bold">
            Alumni Analysis Dashboard
          </MDTypography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab
                  label="Batch-wise"
                  icon={<SchoolIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="District-wise"
                  icon={<LocationOnIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Designation-wise"
                  icon={<WorkIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Location-wise"
                  icon={<LocationOnIcon />}
                  iconPosition="start"
                />
                <Tab
                  label="Combination Reports"
                  icon={<AnalyticsIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>

            {/* Batch-wise Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="primary"
                      title="Alumni by Batch"
                      description="Total alumni distribution across batches"
                      date="Current data"
                      chart={batchAlumniChart}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="success"
                      title="Registered Alumni by Batch"
                      description="Registered alumni distribution"
                      date="Current data"
                      chart={batchRegisteredChart}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12}>
                  <MDBox mb={3}>
                    <ReportsLineChart
                      color="warning"
                      title="Unregistered Alumni by Batch"
                      description="Unregistered alumni distribution across batches"
                      date="Current data"
                      chart={batchUnregisteredChart}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </TabPanel>

            {/* District-wise Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <VerticalBarChart
                      icon={{ color: "info", component: "bar_chart" }}
                      title="Alumni by District"
                      description="Total alumni distribution by district"
                      height="300px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.districtWise?.alumniByDistrict?.labels,
                            analyticsData.districtWise?.alumniByDistrict?.values,
                            "info"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <VerticalBarChart
                      icon={{ color: "success", component: "bar_chart" }}
                      title="Registered Alumni by District"
                      description="Registered alumni by district"
                      height="300px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.districtWise?.registeredByDistrict?.labels,
                            analyticsData.districtWise?.registeredByDistrict?.values,
                            "success"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Designation-wise Tab */}
            <TabPanel value={tabValue} index={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <MDBox mb={3}>
                    <HorizontalBarChart
                      icon={{ color: "warning", component: "work" }}
                      title="Top Designations"
                      description="Top 15 designations by alumni count"
                      height="400px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.designationWise?.topDesignations?.labels?.slice(0, 15),
                            analyticsData.designationWise?.topDesignations?.values?.slice(0, 15),
                            "warning"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox mb={3}>
                    <PieChart
                      icon={{ color: "info", component: "pie_chart" }}
                      title="Designation Distribution"
                      description="Overall distribution"
                      height="400px"
                      chart={analyticsData
                        ? preparePieChartData(
                            analyticsData.designationWise?.designationDistribution?.labels,
                            analyticsData.designationWise?.designationDistribution?.values,
                            "Designation"
                          )
                        : { labels: [], datasets: {} }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Location-wise Tab */}
            <TabPanel value={tabValue} index={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <MDBox mb={3}>
                    <HorizontalBarChart
                      icon={{ color: "error", component: "location_on" }}
                      title="Alumni by Location"
                      description="Top 20 locations by alumni count"
                      height="400px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.locationWise?.alumniByLocation?.labels?.slice(0, 20),
                            analyticsData.locationWise?.alumniByLocation?.values?.slice(0, 20),
                            "error"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDBox mb={3}>
                    <PieChart
                      icon={{ color: "success", component: "pie_chart" }}
                      title="Availability Distribution"
                      description="India vs Abroad"
                      height="400px"
                      chart={analyticsData
                        ? preparePieChartData(
                            analyticsData.locationWise?.alumniByAvailability?.labels,
                            analyticsData.locationWise?.alumniByAvailability?.values,
                            "Availability"
                          )
                        : { labels: [], datasets: {} }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Combination Reports Tab */}
            <TabPanel value={tabValue} index={4}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="primary"
                      title="Batch vs District"
                      description="Alumni distribution by batch and district combination"
                      date="Combined analysis"
                      chart={batchDistrictChart}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <VerticalBarChart
                      icon={{ color: "info", component: "analytics" }}
                      title="Designation vs Location"
                      description="Top combinations"
                      height="350px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.combinationReports?.designationVsLocation?.labels?.slice(0, 20),
                            analyticsData.combinationReports?.designationVsLocation?.values?.slice(0, 20),
                            "info"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <VerticalBarChart
                      icon={{ color: "warning", component: "analytics" }}
                      title="Batch vs Designation"
                      description="Top combinations"
                      height="350px"
                      chart={analyticsData
                        ? prepareVerticalBarChartData(
                            analyticsData.combinationReports?.batchVsDesignation?.labels?.slice(0, 20),
                            analyticsData.combinationReports?.batchVsDesignation?.values?.slice(0, 20),
                            "warning"
                          )
                        : { labels: [], datasets: [] }}
                    />
                  </MDBox>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

AdminAlumniAnalysisDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminAlumniAnalysisDialog;

